import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { signatureService } from '../services/signatureService';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import SignatureDetails from '../components/SignatureDetails';
import { useAuth } from '../contexts/AuthContext';

/**
 * SignatureView page for viewing a specific signature
 */
const SignatureView = () => {
  const { signatureId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  
  // Check if user is authorized to view this signature
  useEffect(() => {
    const checkAuthorization = async () => {
      try {
        setLoading(true);
        
        if (!user) {
          setAuthorized(false);
          setLoading(false);
          return;
        }
        
        // Try to access the signature to see if user is authorized
        await signatureService.getSignature(signatureId);
        setAuthorized(true);
      } catch (error) {
        console.error('Authorization check failed:', error);
        setAuthorized(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkAuthorization();
  }, [signatureId, user]);
  
  // Handle going back to previous page
  const handleBack = () => {
    navigate(-1);
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (!authorized) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-6 max-w-xl mx-auto">
          <div className="flex items-center text-red-500 mb-4">
            <AlertCircle size={24} className="mr-2" />
            <h2 className="text-xl font-semibold">Access Denied</h2>
          </div>
          <p className="text-red-400 mb-6">
            You do not have permission to view this signature. Please log in with an account that has access.
          </p>
          <div className="flex justify-end">
            <button
              onClick={handleBack}
              className="px-4 py-2 bg-gray-700 rounded-md hover:bg-gray-600 flex items-center"
            >
              <ArrowLeft size={18} className="mr-2" />
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center mb-6">
        <button
          onClick={handleBack}
          className="p-2 bg-gray-800 rounded-full hover:bg-gray-700 mr-4"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-semibold">Signature Details</h1>
      </div>
      
      <div className="max-w-4xl mx-auto">
        <SignatureDetails 
          signatureId={signatureId} 
          onClose={handleBack} 
        />
      </div>
    </div>
  );
};

export default SignatureView; 